import { HiddenAxios } from '@/functions/axios-functions';
import type { PromotionType } from '@/states/promotion-state';
import {
  FieldDescriptor,
  ImportConnection,
  ImportFilters,
  ImportedJobDetail,
  ImportedJobSummary,
  ImportFormat,
  ImportProvider,
  ImportRules,
  JsonObject,
  RulePreview,
  RuleSet,
} from './types';

const endpoint = (action: string) => `${process.env.VUE_APP_API_BASE}api/ImportedJobs/${action}`;

export const importedJobsClient = {
  async providers(): Promise<ImportProvider[]> {
    return (await HiddenAxios.get<{ data: ImportProvider[] }>(endpoint('Providers'))).data.data;
  },
  async list(filters: ImportFilters) {
    const response = await HiddenAxios.get<{
      total: number;
      allCount: number;
      attentionCount: number;
      reasonCounts?: Record<string, number>;
      data: ImportedJobSummary[];
    }>(
      endpoint('List'),
      {
        params: {
          view: filters.view,
          search: filters.search || undefined,
          format: filters.format || undefined,
          companyId: filters.company || undefined,
          status: filters.status || undefined,
          reason: filters.reason || undefined,
          offset: (filters.page - 1) * 25,
          take: 25,
        },
      }
    );

    return response.data;
  },
  async detail(id: string) {
    return (await HiddenAxios.get<ImportedJobDetail>(endpoint('Detail'), { params: { id } })).data;
  },
  async connections() {
    return (await HiddenAxios.get<{ data: ImportConnection[] }>(endpoint('Connections'))).data.data;
  },
  async fields(format: ImportFormat, serviceId?: number, id?: string) {
    return (
      await HiddenAxios.get<{ data: FieldDescriptor[] }>(endpoint('Fields'), {
        params: { format, serviceId, id },
      })
    ).data.data;
  },
  async corrections(
    id: string,
    expectedVersion: string,
    changes: JsonObject,
    resetPaths: string[]
  ) {
    return (
      await HiddenAxios.post<ImportedJobDetail>(endpoint('Corrections'), {
        id,
        expectedVersion,
        changes,
        resetPaths,
      })
    ).data;
  },
  async choosePromotion(id: string, expectedVersion: string, promotion: PromotionType) {
    return (
      await HiddenAxios.post<ImportedJobDetail>(endpoint('ChoosePromotion'), {
        id,
        expectedVersion,
        promotion,
      })
    ).data;
  },
  async clearPromotionChoice(id: string, expectedVersion: string) {
    return (
      await HiddenAxios.post<ImportedJobDetail>(endpoint('ClearPromotionChoice'), {
        id,
        expectedVersion,
      })
    ).data;
  },
  async action(action: 'Reevaluate' | 'Approve' | 'Retry', id: string, expectedVersion: string) {
    return (await HiddenAxios.post<ImportedJobDetail>(endpoint(action), { id, expectedVersion }))
      .data;
  },
  async rules(format: ImportFormat, serviceId: number | null) {
    return (
      await HiddenAxios.get<ImportRules>(endpoint('Rules'), {
        params: { format, serviceId: serviceId || undefined },
      })
    ).data;
  },
  async saveRules(
    format: ImportFormat,
    serviceId: number | null,
    expectedVersion: string,
    rules: RuleSet | null,
    manualApproval: boolean | null
  ) {
    return (
      await HiddenAxios.post<ImportRules>(endpoint('SaveRules'), {
        format,
        serviceId,
        expectedVersion,
        rules,
        manualApproval,
      })
    ).data;
  },
  // A connection's paid promotion bought instead of holding a job for credits; null holds the job.
  async outOfCreditsPromotion(serviceId: number) {
    return (
      await HiddenAxios.get<{ promotionType: PromotionType | null }>(endpoint('OutOfCreditsPromotion'), {
        params: { serviceId },
      })
    ).data.promotionType;
  },
  async saveOutOfCreditsPromotion(serviceId: number, promotionType: PromotionType) {
    return (
      await HiddenAxios.post<{ promotionType: PromotionType | null }>(endpoint('SaveOutOfCreditsPromotion'), {
        serviceId,
        promotionType,
      })
    ).data.promotionType;
  },
  async previewRules(
    format: ImportFormat,
    serviceId: number | null,
    rules: RuleSet | null,
    page: number
  ) {
    return (
      await HiddenAxios.post<RulePreview>(endpoint('PreviewRules'), {
        format,
        serviceId,
        rules,
        offset: (page - 1) * 25,
        take: 25,
      })
    ).data;
  },
};
